import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentStep,
  updateFormData,
} from "../../store/actions/formActions";
import {
  // CRM_SUBMIT_ENDPOINT,
  STORE_CLIENT_DATA_ONLY,
  formPages,
} from "../../constants";
import { detectBrand } from "../../utils/brandDetection";
import { identifyKlaviyo, trackKlaviyo } from "../../utils/klaviyo";
import { BRAND_IDS, MLM_ACTIVE_VARIATION } from "../../config/brands";
//import IntroCheckDetails from '../../components/form-pages/IntroCheckDetails';
import IntroCheckDetails from "../../components/form-pages/IntroCheckDetails";
import NewClaimantDetails from "../../components/form-pages/NewClaimantDetails";
import AddressDetails from "../../components/form-pages/AddressDetails";
import ContactDetails from "../../components/form-pages/ContactDetails";
//import RegistrationDetails from '../../components/form-pages/RegistrationDetails'
//import RegistrationResults from '../../components/form-pages/RegistrationResults';
import Signature from "../../components/form-pages/Signature";
import ConfirmDetails from "../../components/form-pages/ConfirmDetails";
import RequestFinanceLender from "../../components/form-pages/RequestFinanceLender";
//import ConfirmLenders from '../../components/form-pages/ConfirmLenders';
//import PreviousAddress from '../../components/form-pages/PreviousAddress';
import CreditCheckSuccess from "../../components/form-pages/CreditCheckSuccess";
import Summary from "../../components/form-pages/Summary";
import Success from "../../components/form-pages/Success";
import PostSuccess from "../../components/form-pages/PostSuccess";
import NoClaim from "../../components/form-pages/NoClaim";
import PageLayout from "../../layout/PageLayout";
import {
  faRotate,
  faShareNodes,
  faSignature,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import Error from "../../components/form-pages/Error";
import AddressLookup from "../../components/form-pages/AddressLookup";
library.add(faRotate, faShareNodes, faSignature, faTrashCan);

const getFormPageName = (step) =>
  Object.entries(formPages).find(([, value]) => value === step)?.[0] ??
  "StartPage";

const getFormPageUrl = (pageName) => {
  const url = new URL(window.location.href);
  url.pathname = `/${pageName}/`;
  return url.toString();
};

export default function CarFinance() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const dispatch = useDispatch();
  const brand = detectBrand();

  // Determine which badge icon to use based on brand
  const isMLMVariation2 =
    brand.id === BRAND_IDS.MLM && MLM_ACTIVE_VARIATION === 2;
  const badgeIcon = isMLMVariation2
    ? "/icons/lucid-icons/badge-check-red.svg"
    : "/icons/lucid-icons/badge-check.svg";

  const { currentStep, compensate } = useSelector((state) => state.compensate);
  const currentPage = getFormPageName(currentStep);

  const onBack = (step, currentData = {}) => {
    // Save current form data before going back
    if (Object.keys(currentData).length > 0) {
      const updatedData = {
        ...compensate,
        ...currentData,
      };
      dispatch(updateFormData(updatedData));
    }
    dispatch(setCurrentStep(step));
  };

  const onNext = (data, step) => {
    const updatedData = {
      ...compensate,
      ...data,
    };

    // console.log("=== CUSTOMER JOURNEY DATA UPDATE ===");
    // console.log("Current Step:", currentStep);
    // console.log("Moving to Step:", step);
    // console.log("New Data Added:", data);
    // console.log("Complete Customer Data:", updatedData);
    // console.log("=====================================");

    dispatch(setCurrentStep(step));
    dispatch(updateFormData(updatedData));
  };

  const onSubmit = async (data, step) => {
    setIsSubmitted(true);

    const finalData = {
      ...compensate,
      ...data,
    };

    // console.log("=== FINAL SUBMISSION - COMPLETE CUSTOMER JOURNEY DATA ===");
    // console.log("Final Step Data:", data);
    // console.log("COMPLETE CUSTOMER DATA:", finalData);
    // console.log("=========================================================");

    dispatch(updateFormData(finalData));

    try {
      // Email is now sent only from Signature page, not here
      // const response = await axios.post(
      //   CRM_SUBMIT_ENDPOINT(),
      //   getCaseInception(finalData)
      // );

      // console.log("=== SUBMISSION RESPONSES ===");
      // console.log("CRM Response:", response);
      // console.log("============================");

      dispatch(setCurrentStep(step));
    } catch (err) {
      // console.error("=== SUBMISSION ERROR ===");
      // console.error("Error Details:", err);
      console.error("Error Response:", err.response?.data);
      console.error("========================");

      dispatch(
        updateFormData({
          ...compensate,
          error: err.response?.data?.message?.detail || "An error occurred",
        }),
      );
      dispatch(setCurrentStep(formPages.Error));
    } finally {
      setIsSubmitted(false);
    }
  };

  // console.log("=== CURRENT STATE ===");
  // console.log("Current Step:", currentStep);
  // console.log("Current Step Name:", Object.keys(formPages)[currentStep]);
  // console.log("Complete Compensate State:", compensate);
  // console.log("=====================");

  // Store client data without email when reaching CreditCheckSuccess or Success
  // Email will be sent from Signature page submit instead
  const submissionInProgressRef = useRef(false);
  const lastSubmittedStepRef = useRef(null);

  useEffect(() => {
    const currentStepName = getFormPageName(currentStep);

    const storeClientDataOnly = async () => {
      // Prevent duplicate submissions
      if (submissionInProgressRef.current) {
        console.log("Submission already in progress, skipping...");
        return;
      }

      // Prevent re-submitting for the same step
      if (lastSubmittedStepRef.current === currentStepName) {
        console.log(
          `Already submitted for step ${currentStepName}, skipping...`,
        );
        return;
      }

      try {
        submissionInProgressRef.current = true;

        // Store client data without triggering email
        const response = await STORE_CLIENT_DATA_ONLY(compensate);

        // Mark this step as submitted
        lastSubmittedStepRef.current = currentStepName;

        // Sync uniqueID with whatever the backend returned
        if (response?.uniqueID && response.uniqueID !== compensate.uniqueID) {
          dispatch(
            updateFormData({
              ...compensate,
              uniqueID: response.uniqueID,
            }),
          );
        }
      } catch (error) {
        console.error("Error storing client data:", error);
      } finally {
        submissionInProgressRef.current = false;
      }
    };

    if (currentStepName === "Success" || currentStepName === "PostSuccess") {
      storeClientDataOnly();
    }
  }, [currentStep, compensate, dispatch]);

  // Scroll to top only when step changes, not on every render
  useEffect(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: "smooth" });
    }, 10);
  }, [currentStep]);

  // Capture IP on page load for Klaviyo and Kount
  useEffect(() => {
    axios.get('/api/public/ip_address').then(res => {
      const ip = res?.data?.address;
      if (ip && !compensate.websiteAquisitionIpAddress) {
        dispatch(updateFormData({ ...compensate, websiteAquisitionIpAddress: ip }));
      }
    }).catch(() => { });
  }, []);

  // Klaviyo tracking — only after email is available
  useEffect(() => {
    if (!compensate.email) return;

    // Tell the browser SDK to identify — links anonymous session (with IP) to profile
    if (typeof window.klaviyo !== 'undefined') {
      window.klaviyo.push([
        'identify',
        {
          email: compensate.email,
          first_name: compensate.firstName || undefined,
          last_name: compensate.surname || undefined,
        },
      ]);
    }

    const pageUrl = getFormPageUrl(currentPage);
    identifyKlaviyo(
      compensate.email,
      compensate.mobile,
      compensate.websiteAquisitionIpAddress,
      undefined,
      {
        firstName: compensate.firstName,
        lastName: compensate.surname,
      },
    );

    trackKlaviyo('Viewed Page', {
      url: pageUrl,
      page_name: currentPage,
      email: compensate.email,
    });
  }, [
    currentPage,
    compensate.email,
    compensate.mobile,
    compensate.firstName,
    compensate.surname,
    compensate.websiteAquisitionIpAddress,
  ]);

  const renderStep = () => {
    switch (currentStep) {
      //  case formPages.IntroCheckDetails:
      //  return <IntroCheckDetails compensate={compensate} onBack={onBack} onNext={onNext} isSubmitted={setIsSubmitted}/>
      case formPages.IntroCheckDetails:
        return (
          <IntroCheckDetails
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
            isSubmitted={setIsSubmitted}
          />
        );

      case formPages.NewClaimantDetails:
        return (
          <NewClaimantDetails
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      case formPages.ContactDetails:
        return (
          <ContactDetails
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
            isSubmitted={setIsSubmitted}
          />
        );
      case formPages.AddressDetails:
        return (
          <AddressDetails
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      case formPages.AddressLookup:
        return (
          <AddressLookup
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      /*case formPages.RegistrationDetails:
        return <RegistrationDetails compensate={compensate} onBack={onBack} onNext={onNext} isSubmitted={setIsSubmitted}/>
      case formPages.RegistrationResults:
        return <RegistrationResults compensate={compensate} onBack={onBack} onNext={onNext}/>
      case formPages.PreviousAddress:
        return <PreviousAddress compensate={compensate} onBack={onBack} onNext={onNext} />*/
      case formPages.Signature:
        return (
          <Signature
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
            isSubmitted={isSubmitted}
            setIsSubmitted={setIsSubmitted}
          />
        );
      case formPages.ConfirmDetails:
        return (
          <ConfirmDetails
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      case formPages.CreditCheckSuccess:
        return (
          <CreditCheckSuccess
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      case formPages.RequestFinanceLender:
        return (
          <RequestFinanceLender
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      /*case formPages.ConfirmLenders:
        return <ConfirmLenders compensate={compensate} onBack={onBack} onNext={onNext}/>*/
      case formPages.Summary:
        return (
          <Summary
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
            isSubmitted={setIsSubmitted}
            onSubmit={onSubmit}
          />
        );
      case formPages.Success:
        return (
          <Success compensate={compensate} onBack={onBack} onNext={onNext} />
        );
      case formPages.PostSuccess:
        return (
          <PostSuccess
            compensate={compensate}
            onBack={onBack}
            onNext={onNext}
          />
        );
      case formPages.NoClaim:
        return (
          <NoClaim compensate={compensate} onBack={onBack} onNext={onNext} />
        );
      case formPages.Error:
        return <Error compensate={compensate} />;
      default:
        return <></>;
    }
  };

  const renderProgressBar = () => {
    const totalSteps = 5;

    // Map actual step indices to display step numbers (1-5)
    const stepMapping = {
      [formPages.IntroCheckDetails]: 0, // Not shown in progress
      [formPages.NewClaimantDetails]: 1, // Step 1
      [formPages.ContactDetails]: 2, // Step 2
      [formPages.AddressDetails]: 3, // Step 3
      [formPages.AddressLookup]: 3, // Step 3 (same as AddressDetails)
      [formPages.Signature]: 4, // Step 4
      [formPages.ConfirmDetails]: 4, // Step 4 (same as Signature, before signing)
      [formPages.RequestFinanceLender]: 5, // Step 5
    };

    const currentStepNumber = stepMapping[currentStep] || 0;
    const progressPercentage = (100 / (totalSteps - 1)) * (currentStepNumber - 1);

    // Hide progress bar on terminal pages and Confirm Details
    const hideProgressBar = [
      formPages.Success,
      formPages.NoClaim,
      formPages.Error,
      formPages.ConfirmDetails,
    ].includes(currentStep);

    // Hide step counter on ConfirmDetails and all subsequent pages
    const hideStepCounter = compensate.detailsConfirmed || currentStep === formPages.ConfirmDetails || currentStep === formPages.PostSuccess;

    return (
      <div
        className="stepper-loader-wrapper"
        style={{ display: hideProgressBar ? "none" : "block" }}
      >
        {currentStepNumber > 0 && !hideStepCounter && (
          <div className="step-counter">
            step {currentStepNumber}/{totalSteps}
          </div>
        )}
        <div className="stepper-loader align-items-center">
          <div style={{ width: `${progressPercentage}%` }}></div>
          {currentStepNumber > 0 && !hideStepCounter && (
            <span
              className="progress-car-icon"
              style={{ left: `clamp(3%, ${progressPercentage}%, 97%)` }}
              role="img"
              aria-label="progress indicator"
            >
              <img
                src="/icons/car_icon.png"
                alt="progress car"
                style={{
                  width: "2em",
                  height: "2em",
                  display: "block",
                }}
              />
            </span>
          )}
        </div>
      </div>
    );
  };

  //track page by variable
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "pageview",
      page_location: window.location.href,
      page_title: "StartPage",
    });
  }, []);

  useEffect(() => {
    const pageUrl = getFormPageUrl(currentPage);
    window.history.pushState({}, "", pageUrl);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "pageview",
      page_location: pageUrl,
      page_title: currentPage,
    });
  }, [currentPage]);

  // Features data for rotation - use brand-specific badge icon
  const features = [
    { text: "Free to check", icon: badgeIcon },
    { text: "60 seconds to make your claim", icon: badgeIcon },
    { text: "AI powered for faster processing", icon: badgeIcon },
  ];

  // Auto-rotate features on mobile
  useEffect(() => {
    const checkIsMobile = () => window.innerWidth <= 768;
    let interval;

    const startRotation = () => {
      if (checkIsMobile()) {
        interval = setInterval(() => {
          setCurrentFeatureIndex(
            (prevIndex) => (prevIndex + 1) % features.length,
          );
        }, 3000); // Rotate every 3 seconds
      }
    };

    const stopRotation = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleResize = () => {
      stopRotation();
      startRotation();
    };

    // Start rotation initially
    startRotation();

    // Listen for window resize events
    window.addEventListener("resize", handleResize);

    return () => {
      stopRotation();
      window.removeEventListener("resize", handleResize);
    };
  }, [features.length]);

  return (
    <PageLayout hideRating={currentStep === formPages.ConfirmDetails}>
      <div className={currentPage}></div>
      <div className="container">
        <div className="fdc-form-wrapp">
          <div className="fdc-stepper-wrapp">
            {renderProgressBar()}
            {currentStep !== formPages.ConfirmDetails && (
              <>
                {/* Desktop view - show all features */}
                <p className="features-desktop">
                  <img src={badgeIcon} alt="tick-badge" />
                  <b>Free to check</b>
                  <img src={badgeIcon} alt="tick-badge" />
                  <b>60 seconds to make your claim</b>
                  <img src={badgeIcon} alt="tick-badge" />
                  <b>AI powered for faster processing</b>
                </p>

                {/* Mobile view - rotating single feature */}
                <div className="features-mobile">
                  <p className="rotating-feature">
                    <img
                      src={features[currentFeatureIndex].icon}
                      alt="tick-badge"
                    />
                    <b>{features[currentFeatureIndex].text}</b>
                  </p>
                </div>
              </>
            )}
          </div>

          <div
            className={`fdc-shadow-block ${isSubmitted ? "active-spinner" : ""
              }`}
          >
            <div className="loading-overlay"></div>
            <div className="loader"></div>
            {renderStep()}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
